import { css } from '@emotion/css';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { NormalButton } from 'src/components/shared/buttons/NormalButton';
import { CharacterImage } from 'src/components/shared/CharacterImage';
import { replaceAllSpaces, throttle } from 'src/lib/utils';
import { UserResortProfile } from 'src/models/resort/UserResortProfile';

interface ReportMenuContentProps {
  basePath: string;
  profile: UserResortProfile;
  isFollowing: boolean;
}

export const ReportMenuContent: React.FC<ReportMenuContentProps> = ({
  basePath,
  profile,
  isFollowing
}) => {
  const { t } = useTranslation(['resort', 'best_friends', 'translation']);
  const history = useHistory();
  const isReported = !!profile.screening;
  const isConstructionEmpty = profile.constructions.length === 0;

  const handleProfileReportClick = useCallback(() => {
    history.replace(`${basePath}/report?follow=${isFollowing}`);
  }, [history, basePath, isFollowing]);

  const handleConstructionReportClick = useCallback(() => {
    history.replace(`${basePath}/select-construction`);
  }, [history, basePath]);

  const handleCancelClick = useCallback(
    throttle(() => {
      history.goBack();
    }, 1000),
    [history]
  );

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;

        .image {
          width: 88px;
          height: 88px;
          margin: 0 auto;
        }
        .name {
          padding: 8px 0;
          color: #4d3906;
        }
        .button {
          width: 100%;
          margin-top: 16px;
        }
      `}
    >
      <div className="image">
        <CharacterImage
          src={profile.image}
          isPlayer={true}
          isReported={isReported}
          imageAriaLabel={t('best_friends:aria_player_image')}
        />
      </div>
      <p className="name">
        {isReported ? t('translation:reported_player') : replaceAllSpaces(profile.name)}
      </p>
      <div className="button">
        <NormalButton onClick={handleProfileReportClick}>{t('report_profile')}</NormalButton>
      </div>
      <div className="button">
        <NormalButton onClick={handleConstructionReportClick} disabled={isConstructionEmpty}>
          {t('report_resort')}
        </NormalButton>
      </div>
      <div className="button">
        <NormalButton onClick={handleCancelClick}>
          {t('translation:dialog_button_cancel')}
        </NormalButton>
      </div>
    </div>
  );
};
